import { useId, type ReactNode } from 'react';
import {
  IrisMark,
  irisFieldClass,
  type IrisFieldMark,
} from '@/components/molecules/IrisFilledMark';
import { MapPin, Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import { GoogleAddressAutocomplete } from '@/components/molecules';
import { cn } from '@/lib/utils';
import { AddressCountrySelect } from './AddressCountrySelect';
import { AddressRoleChip } from './AddressRoleChip';
import { nanpCountry, type AddressDraft } from './address-draft';

/** Shared address editor. All fields update the parent draft immediately;
 * the enclosing contact/address form owns validation and persistence. */
export function ContactAddressForm({
  value,
  onChange,
  title,
  onToggleService,
  onToggleBilling,
  onTogglePrimary,
  canRemove,
  onRemove,
  customFieldsSlot,
  irisMarks,
}: {
  value: AddressDraft;
  onChange: (patch: Partial<AddressDraft>) => void;
  title: string;
  onToggleService: () => void;
  onToggleBilling: () => void;
  onTogglePrimary: () => void;
  canRemove?: boolean;
  onRemove?: () => void;
  customFieldsSlot?: ReactNode;
  irisMarks?: Partial<Record<'street' | 'street2' | 'city' | 'state' | 'zip', IrisFieldMark>>;
}) {
  const country = nanpCountry(value.country);
  const id = useId();

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2.5">
        <MapPin className="size-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-semibold">{title}</span>
        {value.isPrimary ? (
          <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
            Primary
          </span>
        ) : null}
        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto size-7 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
            aria-label={`Remove ${title.toLowerCase()}`}
          >
            <Trash2 size={14} />
          </Button>
        ) : null}
      </div>

      <div className="space-y-3 p-3">
        <div className="flex flex-wrap gap-2">
          <AddressRoleChip
            label="Service"
            active={value.isService}
            onClick={onToggleService}
            tooltip="Where work happens — drives the map and tax."
          />
          <AddressRoleChip
            label="Billing"
            active={value.isBilling}
            onClick={onToggleBilling}
            tooltip="Where invoices are sent."
          />
          <AddressRoleChip
            label="Primary"
            active={value.isPrimary}
            disabled={!value.isService}
            onClick={onTogglePrimary}
            tooltip={
              value.isService
                ? 'The main service address. Only one per contact.'
                : 'Mark this address as Service to make it Primary.'
            }
          />
        </div>

        <div className="space-y-2.5">
          <div className="grid gap-2.5 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`${id}-title`}>Title</Label>
              <Input
                id={`${id}-title`}
                value={value.title}
                onChange={(event) => onChange({ title: event.target.value })}
                placeholder="e.g., Main Office"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${id}-label`}>Label</Label>
              <Input
                id={`${id}-label`}
                value={value.label}
                onChange={(event) => onChange({ label: event.target.value })}
                placeholder="e.g., Vacation Home"
              />
            </div>
          </div>

          <GoogleAddressAutocomplete
            id={`${id}-street`}
            label={
              <>
                Street <IrisMark mark={irisMarks?.street} />
              </>
            }
            inputClassName={irisFieldClass(irisMarks?.street)}
            placeholder="Search address…"
            value={value.street1}
            onChange={(next) => onChange({ street1: next })}
            onAddressSelect={(parsed) =>
              // Autofill only — picking a suggestion keeps the form open so the
              // user can keep editing City/Title/Tax/etc.
              onChange({
                street1: parsed.street,
                street2: parsed.street2 || value.street2,
                city: parsed.city,
                state: parsed.state,
                postalCode: parsed.zip,
                country: nanpCountry(parsed.country || value.country),
              })
            }
          />
          <div className="space-y-1">
            <Label htmlFor={`${id}-street2`}>
              Street line 2 <IrisMark mark={irisMarks?.street2} />
            </Label>
            <Input
              id={`${id}-street2`}
              className={irisFieldClass(irisMarks?.street2)}
              value={value.street2}
              onChange={(event) => onChange({ street2: event.target.value })}
              placeholder="Apt, suite, unit"
            />
          </div>

          <div className="grid gap-2.5 sm:grid-cols-[1fr_5.5rem_7rem_12rem]">
            <div className="space-y-1">
              <Label htmlFor={`${id}-city`}>
                City <IrisMark mark={irisMarks?.city} />
              </Label>
              <Input
                id={`${id}-city`}
                className={irisFieldClass(irisMarks?.city)}
                value={value.city}
                onChange={(event) => onChange({ city: event.target.value })}
                placeholder="City"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${id}-state`}>
                State / Prov. <IrisMark mark={irisMarks?.state} />
              </Label>
              <Input
                id={`${id}-state`}
                className={irisFieldClass(irisMarks?.state)}
                value={value.state}
                onChange={(event) =>
                  onChange({
                    state: event.target.value.toUpperCase().slice(0, 2),
                  })
                }
                placeholder="ST"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${id}-zip`}>
                ZIP / Postal <IrisMark mark={irisMarks?.zip} />
              </Label>
              <Input
                id={`${id}-zip`}
                className={irisFieldClass(irisMarks?.zip)}
                value={value.postalCode}
                onChange={(event) => onChange({ postalCode: event.target.value })}
                placeholder="ZIP"
              />
            </div>
            <div className="space-y-1">
              <Label>Country</Label>
              <AddressCountrySelect
                value={country}
                onChange={(code) => onChange({ country: code })}
              />
            </div>
          </div>

          <div className="grid gap-x-3 gap-y-2.5 sm:grid-cols-[auto_1fr_12rem] sm:items-end">
            <label className="flex h-9 items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
              <Switch
                checked={value.isTaxExempt}
                onCheckedChange={(checked) => onChange({ isTaxExempt: checked })}
              />
              Tax exempt
            </label>
            <div className="space-y-1">
              <Label className={cn(!value.isTaxExempt && 'text-muted-foreground/60')}>
                Certificate
              </Label>
              <Input
                value={value.taxExemptCertificate}
                onChange={(event) => onChange({ taxExemptCertificate: event.target.value })}
                disabled={!value.isTaxExempt}
                placeholder="Certificate #"
              />
            </div>
            <div className="space-y-1">
              <Label className={cn(!value.isTaxExempt && 'text-muted-foreground/60')}>Expiry</Label>
              <Input
                type="date"
                value={value.taxExemptExpiry}
                onChange={(event) => onChange({ taxExemptExpiry: event.target.value })}
                disabled={!value.isTaxExempt}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Access notes</Label>
            <Textarea
              value={value.accessNotes}
              onChange={(event) => onChange({ accessNotes: event.target.value })}
              placeholder="Access notes — gate code, dog, parking, etc."
              rows={2}
            />
          </div>

          {customFieldsSlot}
        </div>
      </div>
    </div>
  );
}
